import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useFriendActions } from '@/features/friends/hooks/use-friend-actions';
import { useFriendshipStatus } from '@/features/friends/hooks/use-friendship-status';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useAppTheme } from '@/providers/theme-provider';

interface ProfileActionsProps {
  isCurrentUser?: boolean;
  userId: string;
  onEditProfile?: () => void;
}

export function ProfileActions({
  isCurrentUser = false,
  userId,
}: ProfileActionsProps) {
  const theme = useAppTheme();
  const { data: friendshipStatus, isLoading: isLoadingStatus } = useFriendshipStatus(userId, {
    enabled: !isCurrentUser,
  });
  const { sendRequest, acceptRequest, rejectRequest } = useFriendActions();
  const isSendingRequest = sendRequest.isPending;
  const isAccepting = acceptRequest.isPending;
  const isRejecting = rejectRequest.isPending;

  const handleAddFriend = () => {
    sendRequest.mutate(userId);
  };

  if (isCurrentUser) {
    return null;
  }

  if (isLoadingStatus) {
    return (
      <View className="flex-row items-center justify-center gap-2 p-4 rounded-lg mb-4">
        <ActivityIndicator size="small" color={theme.colors.primary} />
        <Text className="text-sm font-medium text-muted-foreground">
          Loading...
        </Text>
      </View>
    );
  }

  const status = friendshipStatus?.status;
  const isPendingSent = status === 'pending_sent';
  const isPendingReceived = status === 'pending_received';
  const isFriends = status === 'friends';

  if (isFriends) {
    return null;
  }

  if (isPendingSent) {
    return (
      <View
        className="flex-row items-center justify-center gap-2 p-4 rounded-lg mb-4 bg-surface-secondary border border-border"
      >
        <Ionicons name="time-outline" size={20} className="text-muted-foreground" />
        <Text className="text-sm font-medium text-muted-foreground">
          Friend Request Sent
        </Text>
      </View>
    );
  }

  if (isPendingReceived && friendshipStatus?.requestId) {
    const isProcessing = isAccepting || isRejecting;
    return (
      <View className="flex-row gap-2 mb-4">
        <Button
          variant="primary"
          className="flex-1"
          onPress={() => acceptRequest.mutate(friendshipStatus.requestId!)}
          disabled={isProcessing}
          loading={isAccepting}
          accessibilityRole="button"
          accessibilityLabel={isAccepting ? 'Accepting friend request' : 'Accept friend request'}
        >
          <Ionicons name="checkmark" size={20} className="text-primary-foreground" />
          <Text className="text-base font-sans-semibold text-primary-foreground">
            {isAccepting ? 'Accepting...' : 'Accept Request'}
          </Text>
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          onPress={() => rejectRequest.mutate(friendshipStatus.requestId!)}
          disabled={isProcessing}
          loading={isRejecting}
          accessibilityRole="button"
          accessibilityLabel={isRejecting ? 'Ignoring friend request' : 'Ignore friend request'}
        >
          <Ionicons name="close" size={20} className="text-foreground" />
          <Text className="text-base font-sans-semibold text-foreground">
            {isRejecting ? 'Ignoring...' : 'Ignore'}
          </Text>
        </Button>
      </View>
    );
  }

  return (
    <View className="mb-4">
      <Button
        variant="primary"
        onPress={handleAddFriend}
        disabled={isSendingRequest}
        loading={isSendingRequest}
        accessibilityRole="button"
        accessibilityLabel={isSendingRequest ? 'Sending friend request' : 'Add friend'}
      >
        <Ionicons name="person-add" size={20} className="text-primary-foreground" />
        <Text className="text-sm font-sans-semibold text-primary-foreground">
          {isSendingRequest ? 'Sending...' : 'Add Friend'}
        </Text>
      </Button>
    </View>
  );
}
